import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Toast, showApolloErrorToUser, useConfirm } from '@pletly/ui';

import { useLoadingOverlay } from '%context';
import { useDeletePostMutation } from './__generated__/deletePost.mutation.generated';

interface Params {
	postId: string;
	onSuccess?: () => unknown;
	refetchQueries?: NonNullable<Parameters<typeof useDeletePostMutation>[0]>['refetchQueries'];
}
export const useDeletePost = ({ postId, onSuccess, refetchQueries }: Params) => {
	const { t } = useTranslation();
	const { confirm } = useConfirm();
	const { showLoadingOverlay, closeLoadingOverlay } = useLoadingOverlay();

	const [
		deletePostMutation,
		{
			client: { cache },
		},
	] = useDeletePostMutation({
		variables: { postId },
		refetchQueries,
	});

	const deletePost = useCallback(async () => {
		const confirmation = await confirm(
			t('post.delete-post'),
			t('post.are-you-sure-you-want-to-delete-this-post'),
		);

		if (!confirmation) return;

		showLoadingOverlay();
		try {
			await deletePostMutation();
			const id = cache.identify({ __typename: 'Post', id: postId });
			cache.evict({ id });

			onSuccess?.();

			Toast.show({
				type: 'success',
				text1: t('post.post-deleted'),
			});
		} catch (error) {
			showApolloErrorToUser(error as Error);
			console.error(error);
		} finally {
			closeLoadingOverlay();
		}
	}, [
		confirm,
		t,
		showLoadingOverlay,
		deletePostMutation,
		cache,
		postId,
		onSuccess,
		closeLoadingOverlay,
	]);

	return deletePost;
};
